/**
 * UX3: conversations for the current run — continue, rename, or start new.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ConversationTitleEditor, readConversationTitle } from '@/components/ai-studio/conversation';
import type { AiStudioConversationSummary } from '@/types/aiStudio';

interface RunConversationsSidebarProps {
  currentConversationId: string | null;
  conversations: AiStudioConversationSummary[];
  loading: boolean;
  creating: boolean;
  onSelect: (conversationId: string) => void;
  onNew: () => void;
  onRename: (conversationId: string, title: string) => Promise<void>;
}

export const RunConversationsSidebar: React.FC<RunConversationsSidebarProps> = ({
  currentConversationId,
  conversations,
  loading,
  creating,
  onSelect,
  onNew,
  onRename,
}) => {
  const { t } = useLanguage();
  const atLabel = (date: string) => t('aiStudio.workspace.conversationAt', { date });

  const sorted = [...conversations].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );

  const labelFor = (conversation: AiStudioConversationSummary) => {
    const title = readConversationTitle(conversation.shellMetadata, conversation.name);
    if (title) return title;
    return atLabel(new Date(conversation.updatedAt).toLocaleString());
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.workspace.conversations')}
        </h2>
        <button
          type="button"
          onClick={onNew}
          disabled={creating}
          className="text-base text-[var(--color-info)] hover:underline disabled:opacity-50"
        >
          {creating ? t('aiStudio.conversation.creating') : t('aiStudio.conversation.newConversation')}
        </button>
      </div>

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.conversation.loadingConversations')}
        </p>
      ) : sorted.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.workspace.noConversations')}
        </p>
      ) : (
        <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto lg:max-h-56">
          {sorted.map((conversation) => {
            const active = conversation.id === currentConversationId;
            const title = labelFor(conversation);
            return (
              <li key={conversation.id}>
                <div
                  className={`rounded px-2 py-2 ${
                    active
                      ? 'bg-[var(--color-secondary-bg)]'
                      : 'hover:bg-[var(--color-secondary-bg)]/60'
                  }`}
                >
                  {active ? (
                    <ConversationTitleEditor
                      variant="inline"
                      title={title}
                      onSave={(next) => onRename(conversation.id, next)}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelect(conversation.id)}
                      className="w-full text-left"
                    >
                      <span className="block truncate font-medium text-[var(--color-surface-text)]">
                        {title}
                      </span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onSelect(conversation.id)}
                    className="mt-0.5 block w-full text-left text-sm text-[var(--color-surface-text-secondary)] hover:underline"
                  >
                    {new Date(conversation.updatedAt).toLocaleString()}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
